import { type Placement, usePortalPlacement } from "@/hooks/usePortalPlacement";
import { cn } from "@paribus/shared-ui";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const DEFAULT_GAP_PX = 10;
const DEFAULT_ESTIMATED_HEIGHT_PX = 280;
const VIEWPORT_EDGE_MARGIN_PX = 12;
/** Half of ~w-72 (18rem), used for horizontal viewport clamping. */
const DEFAULT_PANEL_HALF_WIDTH_PX = 148;

function computePlacement(el: Element | null, panelHalfWidthPx: number): Placement {
  if (!el) {
    return { top: 0, left: 0, transform: "translateX(-50%)" };
  }

  const rect = el.getBoundingClientRect();
  const spaceAbove = rect.top;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp =
    spaceAbove >= DEFAULT_ESTIMATED_HEIGHT_PX + DEFAULT_GAP_PX ||
    (spaceAbove > spaceBelow && spaceAbove >= 72);

  let left = rect.left + rect.width / 2;
  const edge = panelHalfWidthPx + VIEWPORT_EDGE_MARGIN_PX;
  left = Math.max(edge, Math.min(left, window.innerWidth - edge));

  if (openUp) {
    return {
      top: rect.top - DEFAULT_GAP_PX,
      left,
      transform: "translate(-50%, -100%)",
    };
  }
  return {
    top: rect.bottom + DEFAULT_GAP_PX,
    left,
    transform: "translateX(-50%)",
  };
}

export interface PortalPopoverProps {
  children: ReactNode;
  content: ReactNode;
  /** Extra Tailwind width / max-width classes for the panel. */
  panelClassName?: string;
  /** Approximate half-width in px for horizontal clamping (default matches ~w-72). */
  panelHalfWidthPx?: number;
  title?: string;
  /** `dense`: flat white panel, tight padding — for table-adjacent help popovers. */
  variant?: "default" | "dense";
}

export function PortalPopover({
  children,
  content,
  panelClassName = "w-72",
  panelHalfWidthPx = DEFAULT_PANEL_HALF_WIDTH_PX,
  title,
  variant = "default",
}: PortalPopoverProps) {
  const isDense = variant === "dense";
  const hideTimerRef = useRef<number | null>(null);

  const computePlacementForAnchor = useCallback(
    (anchor: HTMLSpanElement | null) => computePlacement(anchor, panelHalfWidthPx),
    [panelHalfWidthPx]
  );
  const { anchorRef, isOpen, coords, open, close } = usePortalPlacement<HTMLSpanElement>(
    computePlacementForAnchor,
    { trackWhileOpen: true }
  );

  const clearHideTimer = () => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleOpen = () => {
    clearHideTimer();
    open();
  };

  const scheduleClose = () => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      close();
      hideTimerRef.current = null;
    }, 150);
  };

  useEffect(() => () => clearHideTimer(), []);

  return (
    <>
      <span
        ref={anchorRef}
        className="relative inline-flex rounded-md text-text-disabled transition-colors duration-200 hover:text-muted-foreground"
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
      >
        {children}
      </span>
      {isOpen &&
        createPortal(
          <div
            role="tooltip"
            className={cn(
              "fixed overflow-y-auto text-left text-foreground motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95",
              isDense
                ? "max-h-[min(64vh,18rem)] rounded-xl border border-border bg-card p-3 shadow-sm"
                : "max-h-[min(72vh,22rem)] rounded-xl border border-border/90 bg-card/95 p-4 shadow-lg backdrop-blur-md",
              panelClassName
            )}
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.transform,
              zIndex: "var(--z-tooltip)",
            }}
            onMouseEnter={clearHideTimer}
            onMouseLeave={scheduleClose}
          >
            {title ? (
              <p
                className={cn(
                  isDense
                    ? "mb-2 text-xs font-semibold tracking-tight text-muted-foreground"
                    : "mb-3 text-2xs font-semibold uppercase tracking-micro text-muted-foreground"
                )}
              >
                {title}
              </p>
            ) : null}
            <div className="normal-case">{content}</div>
          </div>,
          document.body
        )}
    </>
  );
}
